import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, CheckCircle2, Loader2, ThumbsUp, ThumbsDown, Clock, XCircle, Eye, EyeOff, Flag, Plus, Crown } from "lucide-react";
import { useDiscord } from "../../context/DiscordContext";
import { api } from "../../lib/api";
import { SectionHeader } from "./Features";
import DiscordButton from "./DiscordButton";
import FactionChip from "./FactionChip";
import { FACTION_COLORS, factionTextColor } from "../../lib/factionColors";

const TIMEZONES = ["UTC-8 PST", "UTC-7 MST", "UTC-6 CST", "UTC-5 EST", "UTC+0 GMT", "UTC+1 CET", "UTC+2 EET", "UTC+8 SGT", "UTC+10 AET"];
const RP_LEVELS = [
  { v: "none", l: "Brand new" },
  { v: "some", l: "Some experience" },
  { v: "experienced", l: "Experienced" },
  { v: "veteran", l: "Veteran (3+ years)" },
];

const initialForm = {
  character_name: "",
  dob: "",
  timezone: "UTC-5 EST",
  hours_played: "",
  rp_experience: "some",
  backstory: "",
  scenario_response: "",
  understands_semi_serious_rp: null,
  why_join: "",
  rules_accepted: false,
  voice_chat_ok: false,
  referred_by: "",
  server_username: "",
  server_password: "",
};

const SERVER_USERNAME_RE = /^[A-Za-z0-9_]{3,32}$/;
const DRAFT_KEY = "wlou.whitelist.draft";

// Draft autosave (password excluded) so long answers survive failed submits and re-logins.
function loadDraft() {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
    return d ? { ...initialForm, ...d, server_password: "" } : initialForm;
  } catch {
    return initialForm;
  }
}
function saveDraft(form) {
  try {
    const { server_password, ...rest } = form;
    localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
  } catch { /* storage unavailable */ }
}
function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* storage unavailable */ }
}

export default function Whitelist() {
  const { isConnected, user, inGuild, guildRequired, verifyRequired, verifyUrl, isVerified, connect } = useDiscord();
  const [form, setForm] = useState(loadDraft);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existing, setExisting] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [applicationsOpen, setApplicationsOpen] = useState(true);

  const guildOk = !guildRequired || inGuild;
  const verifiedOk = !verifyRequired || isVerified;
  const canSubmit = isConnected && guildOk && verifiedOk;

  // Fetch whitelist-slot status on mount so the form can flip to a "CLOSED"
  // state when the admin has burned through all available slots.
  useEffect(() => {
    let cancelled = false;
    api.get("/server/stats").then((res) => {
      if (cancelled) return;
      const total = Number(res.data?.whitelist_slots_total) || 0;
      const taken = Number(res.data?.whitelist_slots_taken) || 0;
      setApplicationsOpen(total <= 0 || (total - taken) > 0);
    }).catch(() => { /* keep default open on network failure */ });
    return () => { cancelled = true; };
  }, [submitted]);

  // Fetch the user's existing application (if any) when they sign in.
  useEffect(() => {
    if (!canSubmit) {
      setExisting(null);
      return;
    }
    let cancelled = false;
    setCheckingExisting(true);
    api
      .get("/whitelist/my-application")
      .then((res) => {
        if (!cancelled) setExisting(res.data?.application || null);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCheckingExisting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canSubmit, submitted]);

  useEffect(() => {
    if (!submitted) saveDraft(form);
  }, [form, submitted]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const focusField = (testid) => {
    if (typeof document === "undefined") return;
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Defer focus a tick so smooth-scroll doesn't fight it.
      setTimeout(() => el.focus({ preventScroll: true }), 250);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Explicit field-by-field validation so failures never silently swallow
    // the click — every error gets a toast and scrolls to the offender.
    const backLen = form.backstory.trim().length;
    const scnLen = form.scenario_response.trim().length;
    const checks = [
      [!form.character_name.trim(), "Character name is required.", "field-character-name"],
      [!form.dob, "Date of birth is required.", "field-dob"],
      [!form.hours_played && form.hours_played !== 0, "How many PZ hours have you played?", "field-hours"],
      [!form.backstory.trim(), "Tell us your character's backstory.", "field-backstory"],
      [backLen > 0 && backLen < 100, `Backstory needs at least 100 characters (you have ${backLen}).`, "field-backstory"],
      [!form.scenario_response.trim(), "Answer the scenario question.", "field-scenario"],
      [scnLen > 0 && scnLen < 100, `Scenario answer needs at least 100 characters (you have ${scnLen}).`, "field-scenario"],
      [!form.why_join.trim(), "Tell us why you want to join.", "field-why-join"],
      [form.understands_semi_serious_rp === null, "Please answer the Semi-Serious RP question.", "field-backstory"],
      [!SERVER_USERNAME_RE.test(form.server_username.trim()), "Server username must be 3–32 chars (letters / numbers / underscore).", "field-server-username"],
      [!form.server_password.trim(), "Server password is required.", "field-server-password"],
      [!form.rules_accepted, "You must accept the rules to submit.", "checkbox-rules"],
    ];
    for (const [bad, msg, testid] of checks) {
      if (bad) {
        toast.error(msg);
        focusField(testid);
        return;
      }
    }

    setSubmitting(true);
    try {
      await api.post("/whitelist/applications", {
        character_name: form.character_name.trim(),
        dob: form.dob,
        timezone: form.timezone,
        hours_played: Number(form.hours_played) || 0,
        rp_experience: form.rp_experience,
        backstory: form.backstory.trim(),
        scenario_response: form.scenario_response.trim(),
        understands_semi_serious_rp: form.understands_semi_serious_rp,
        why_join: form.why_join.trim(),
        rules_accepted: form.rules_accepted,
        voice_chat_ok: form.voice_chat_ok,
        referred_by: form.referred_by.trim() || null,
        server_username: form.server_username.trim(),
        server_password: form.server_password,
      });
      clearDraft();
      setSubmitted(true);
      toast.success("Application submitted. Watch your DMs.");
      // The form is much taller than SuccessState. If we don't reset scroll,
      // the user is left staring at empty space below the success card —
      // which reads as a "blank page". Scroll the new state into view.
      requestAnimationFrame(() => {
        const target = document.getElementById("whitelist");
        if (window.__lenis && target) {
          window.__lenis.scrollTo(target, { offset: -72, duration: 0.8 });
        } else if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Your Discord login expired. Your answers are saved, so log back in and hit Submit again.", {
          duration: 15000,
          action: { label: "Log in", onClick: connect },
        });
      } else if (!err?.response || status >= 500) {
        toast.error("Couldn't reach the server. Your answers are saved, so try again in a minute.", { duration: 10000 });
      } else {
        toast.error(err?.response?.data?.detail || "Submission failed. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="whitelist" className="bg-[#0D110C] py-24 sm:py-32 border-t border-[#283524]">
      <div className="max-w-4xl mx-auto px-6">
        <SectionHeader
          eyebrow="// WHITELIST APPLICATION"
          title="Earn your place at the wall."
          desc="No one gets in without telling us who they are. This isn't a form — it's the first scene of your character's story. Make it count."
        />

        {submitted ? (
          <SuccessState user={user} />
        ) : existing ? (
          <>
            <ExistingApplicationState application={existing} />
            {existing.status === "approved" && <CreateFactionBlock />}
          </>
        ) : !applicationsOpen ? (
          <ClosedState />
        ) : checkingExisting && canSubmit ? (
          <div className="mt-16 text-center py-16">
            <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={24} />
          </div>
        ) : (
          <div className="mt-12 relative">
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="whitelist-form"
              className={`bg-[#161D14] border border-[#283524] p-6 sm:p-10 transition-all ${
                !canSubmit ? "opacity-25 blur-[1px] select-none" : "opacity-100"
              }`}
              aria-hidden={!canSubmit}
            >
              <fieldset disabled={!canSubmit} className="space-y-10 border-0 p-0 m-0 min-w-0">
              <FormSection step="01" title="Identity">
                <div className="space-y-5 max-w-md">
                  <Field label="Character name" required>
                    <Input
                      data-testid="field-character-name"
                      value={form.character_name}
                      onChange={(e) => update("character_name", e.target.value)}
                      required
                      maxLength={48}
                    />
                  </Field>
                  <Field label="What is your Real Life Age?" required hint="Date of birth (MM/DD/YYYY).">
                    <Input
                      data-testid="field-dob"
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={form.dob}
                      onChange={(e) => update("dob", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label="Timezone" required>
                    <Select
                      data-testid="field-timezone"
                      value={form.timezone}
                      onChange={(e) => update("timezone", e.target.value)}
                    >
                      {TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </FormSection>

              <FormSection step="02" title="Experience">
                <Grid cols={2}>
                  <Field label="Project Zomboid hours" required>
                    <Input
                      data-testid="field-hours"
                      type="number"
                      min={0}
                      value={form.hours_played}
                      onChange={(e) => update("hours_played", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label="RP experience" required>
                    <Select
                      data-testid="field-rp-experience"
                      value={form.rp_experience}
                      onChange={(e) => update("rp_experience", e.target.value)}
                    >
                      {RP_LEVELS.map((r) => (
                        <option key={r.v} value={r.v}>
                          {r.l}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </Grid>
              </FormSection>

              <FormSection step="03" title="Story">
                <Field
                  label="Character backstory"
                  required
                  hint="Where were they on the day of the Knox Event? How did they survive ten years? A paragraph or two — be concrete."
                >
                  <Textarea
                    data-testid="field-backstory"
                    rows={6}
                    value={form.backstory}
                    onChange={(e) => update("backstory", e.target.value)}
                  />
                  <CharCounter value={form.backstory} min={100} suggested={100} />
                </Field>

                <Field
                  label="Scenario"
                  required
                  hint="Your character finds a small camp at dusk. You see three strangers inside the camp, one is bleeding badly. They haven't seen you yet. How do you proceed?"
                >
                  <Textarea
                    data-testid="field-scenario"
                    rows={5}
                    value={form.scenario_response}
                    onChange={(e) => update("scenario_response", e.target.value)}
                  />
                  <CharCounter value={form.scenario_response} min={100} suggested={100} />
                </Field>

                <Field
                  label="Do you understand what Semi-Serious RP means?"
                  required
                  hint="Semi-Serious RP means staying in character for meaningful interactions, but allowing some flexibility for fun moments — without breaking immersion entirely."
                >
                  <ThumbToggle
                    value={form.understands_semi_serious_rp}
                    onChange={(v) => update("understands_semi_serious_rp", v)}
                  />
                </Field>

                <Field label="Why this server?" required>
                  <Textarea
                    data-testid="field-why-join"
                    rows={3}
                    value={form.why_join}
                    onChange={(e) => update("why_join", e.target.value)}
                  />
                  <CharCounter value={form.why_join} suggested={80} />
                </Field>

                <Field label="Referred by (optional)">
                  <Input
                    data-testid="field-referred-by"
                    placeholder="Discord username"
                    value={form.referred_by}
                    onChange={(e) => update("referred_by", e.target.value)}
                  />
                </Field>
              </FormSection>

              <FormSection step="04" title="Server credentials">
                <p className="text-[13px] text-[#A8B5A0] -mt-1 leading-relaxed">
                  These are the username and password you'll use to log into the Project Zomboid server itself. The
                  admin will copy them into the server config — pick something you'll remember.{" "}
                  <span className="text-[#C0A875]">Write it down somewhere safe.</span>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Desired server username" required>
                    <Input
                      data-testid="field-server-username"
                      placeholder="e.g. survivor_42"
                      maxLength={32}
                      value={form.server_username}
                      onChange={(e) => update("server_username", e.target.value)}
                      autoComplete="off"
                      spellCheck="false"
                      required
                    />
                    <p className="mt-1 font-mono-ui text-[10px] text-[#8D9A87]">
                      // 3–32 chars · letters, numbers, underscore only
                    </p>
                  </Field>
                  <Field label="Server password" required>
                    <PasswordInput
                      data-testid="field-server-password"
                      value={form.server_password}
                      onChange={(e) => update("server_password", e.target.value)}
                      placeholder="Make one up & remember it"
                    />
                    <p className="mt-1 font-mono-ui text-[10px] text-[#8D9A87]">
                      // Any length. Stored so the admin can whitelist you on the server.
                    </p>
                  </Field>
                </div>
              </FormSection>

              <FormSection step="05" title="Acknowledgments">
                <CheckRow
                  testid="checkbox-rules"
                  checked={form.rules_accepted}
                  onChange={(v) => update("rules_accepted", v)}
                  label="I have read all 8 server rules and accept the consequences for breaking them."
                />
                <CheckRow
                  testid="checkbox-voice"
                  checked={form.voice_chat_ok}
                  onChange={(v) => update("voice_chat_ok", v)}
                  label="I am comfortable joining voice chat for in-character RP (recommended, not required)."
                />
              </FormSection>

              <div className="pt-4 border-t border-[#283524] flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                <div className="font-mono-ui text-[10px] text-[#8D9A87] uppercase tracking-[0.25em]">
                  // SUBMITTING AS{" "}
                  <span className="text-[#C0A875]">@{user?.username || "—"}</span>
                </div>
                <button
                  type="submit"
                  data-testid="whitelist-submit"
                  disabled={submitting}
                  className="bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-8 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold transition-colors inline-flex items-center gap-2"
                >
                  {submitting ? <Loader2 className="animate-spin" size={14} /> : null}
                  {submitting ? "Submitting…" : "Submit Application →"}
                </button>
              </div>
              </fieldset>
            </form>

            {/* Locked overlay — two reasons: not connected, or not in guild */}
            {!canSubmit && (
              <div
                data-testid="whitelist-locked-overlay"
                className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 bg-[#0D110C]/40 backdrop-blur-sm border border-[#283524]"
              >
                <div className="w-16 h-16 border-2 border-[#C0A875] flex items-center justify-center mb-6">
                  <Lock className="text-[#C0A875]" size={28} />
                </div>
                {!isConnected ? (
                  <>
                    <span className="label-tag text-[#C0A875]">// AUTHENTICATION REQUIRED</span>
                    <h3 className="mt-3 font-display uppercase text-[#E2E4DF] text-3xl sm:text-4xl">
                      Connect Discord to apply
                    </h3>
                    <p className="mt-3 max-w-md text-[#8D9A87] text-sm leading-relaxed">
                      You must be a member of the What's Left of Us Discord server to submit
                      a whitelist application. Join us, then connect below.
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                      <a
                        href="https://discord.gg/zdE2kWsStf"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="join-discord-link"
                        className="border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] hover:text-[#C0A875] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] transition-colors"
                      >
                        Join the Discord →
                      </a>
                      <DiscordButton />
                    </div>
                  </>
                ) : !guildOk ? (
                  <>
                    <span className="label-tag text-[#C0392B]">// NOT A MEMBER</span>
                    <h3 className="mt-3 font-display uppercase text-[#E2E4DF] text-3xl sm:text-4xl">
                      Join the Discord first
                    </h3>
                    <p className="mt-3 max-w-md text-[#8D9A87] text-sm leading-relaxed">
                      You're signed in as <span className="text-[#C0A875]">@{user?.username}</span>
                      , but we can't see you in the What's Left of Us server. Join via the
                      invite below, then refresh this page.
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                      <a
                        href="https://discord.gg/zdE2kWsStf"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="join-discord-link"
                        className="bg-[#5865F2] hover:bg-[#4752C4] text-white px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] transition-colors"
                      >
                        Join the Discord →
                      </a>
                      <button
                        onClick={() => window.location.reload()}
                        className="border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] hover:text-[#C0A875] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] transition-colors"
                      >
                        I've Joined — Refresh
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="label-tag text-[#C0A875]">// VERIFICATION REQUIRED</span>
                    <h3 className="mt-3 font-display uppercase text-[#E2E4DF] text-3xl sm:text-4xl">
                      Verify before you apply
                    </h3>
                    <p className="mt-3 max-w-md text-[#8D9A87] text-sm leading-relaxed">
                      Welcome <span className="text-[#C0A875]">@{user?.username}</span>. One last
                      gate: complete VaultCord verification so we know you're not a bot. Takes
                      thirty seconds — it'll grant you the Verified role in Discord, then come
                      back here.
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                      <a
                        href={verifyUrl || "https://vaultcord.win/whatsleftofus"}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="verify-vaultcord-link"
                        className="bg-[#C0A875] hover:bg-[#a99362] text-[#0D110C] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold transition-colors"
                      >
                        Verify via VaultCord →
                      </a>
                      <button
                        onClick={() => window.location.reload()}
                        data-testid="verify-refresh-button"
                        className="border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] hover:text-[#C0A875] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] transition-colors"
                      >
                        I've Verified — Refresh
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function SuccessState({ user }) {
  return (
    <div
      data-testid="whitelist-success"
      className="mt-16 bg-[#161D14] border border-[#5C6B51] p-10 text-center"
    >
      <CheckCircle2 className="text-[#C0A875] mx-auto" size={48} />
      <h3 className="mt-6 font-display uppercase text-[#E2E4DF] text-3xl sm:text-4xl">
        Transmission received.
      </h3>
      <p className="mt-4 text-[#8D9A87] max-w-lg mx-auto text-sm leading-relaxed">
        Thanks <span className="text-[#C0A875]">@{user?.username}</span>. Your application is in
        the queue. We review submissions within 24–48 hours. You'll receive a Discord DM with
        the decision.
      </p>
      <p className="mt-2 label-tag">// STAY ALIVE OUT THERE.</p>
    </div>
  );
}

function ClosedState() {
  return (
    <div
      data-testid="whitelist-closed"
      className="mt-16 bg-[#161D14] border border-[#C0392B] p-10 text-center"
    >
      <Lock className="text-[#C0392B] mx-auto" size={44} />
      <span className="mt-4 block label-tag text-[#C0392B]">// APPLICATIONS CLOSED</span>
      <h3 className="mt-2 font-display uppercase text-[#E2E4DF] text-3xl sm:text-4xl">
        The whitelist is full.
      </h3>
      <p className="mt-4 text-[#8D9A87] max-w-lg mx-auto text-sm leading-relaxed">
        Every slot in this intake is claimed. We'll post the next opening in the What's Left of
        Us Discord — join and watch <span className="text-[#C0A875]">#announcements</span> for
        the reopen date.
      </p>
      <a
        href="https://discord.gg/zdE2kWsStf"
        target="_blank"
        rel="noreferrer"
        data-testid="whitelist-closed-discord-link"
        className="mt-6 inline-block bg-[#5865F2] hover:bg-[#4752C4] text-white px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
      >
        Join the Discord →
      </a>
    </div>
  );
}

function ExistingApplicationState({ application }) {
  const submitted = application.submitted_at
    ? new Date(application.submitted_at).toLocaleString()
    : null;
  const reviewed = application.reviewed_at
    ? new Date(application.reviewed_at).toLocaleString()
    : null;

  const layouts = {
    pending: {
      Icon: Clock,
      iconColor: "text-[#C0A875]",
      borderColor: "border-[#C0A875]",
      eyebrow: "// IN REVIEW",
      eyebrowColor: "text-[#C0A875]",
      headline: "Your application is in the queue.",
      body:
        "We review submissions within 24–48 hours. You'll get a Discord DM the moment a decision is made. No need to apply again — only one application per survivor.",
    },
    approved: {
      Icon: CheckCircle2,
      iconColor: "text-[#4ADE80]",
      borderColor: "border-[#4ADE80]",
      eyebrow: "// WHITELIST APPROVED",
      eyebrowColor: "text-[#4ADE80]",
      headline: "Welcome to the wall, survivor.",
      body:
        "Your Discord role has been upgraded to Survivor. Server connection details are pinned in the Discord. See you out there.",
    },
    rejected: {
      Icon: XCircle,
      iconColor: "text-[#C0392B]",
      borderColor: "border-[#C0392B]",
      eyebrow: "// APPLICATION DECLINED",
      eyebrowColor: "text-[#C0392B]",
      headline: "This one wasn't a fit.",
      body:
        "Your application has been reviewed and unfortunately wasn't accepted at this time. If you believe this was a mistake, open a ticket in Discord.",
    },
  };

  const layout = layouts[application.status] || layouts.pending;
  const { Icon } = layout;

  return (
    <div
      data-testid="whitelist-existing"
      className={`mt-16 bg-[#161D14] border ${layout.borderColor} p-10 text-center`}
    >
      <Icon className={`${layout.iconColor} mx-auto`} size={48} />
      <span className={`block label-tag mt-6 ${layout.eyebrowColor}`}>{layout.eyebrow}</span>
      <h3 className="mt-3 font-display uppercase text-[#E2E4DF] text-3xl sm:text-4xl">
        {layout.headline}
      </h3>
      <p className="mt-4 text-[#8D9A87] max-w-xl mx-auto text-sm leading-relaxed">{layout.body}</p>

      <div className="mt-8 inline-flex flex-col sm:flex-row items-stretch border border-[#283524] bg-[#0D110C]">
        <MetaCell label="Character" value={application.character_name || "—"} />
        {submitted && <MetaCell label="Submitted" value={submitted} />}
        {reviewed && <MetaCell label="Reviewed" value={reviewed} last />}
      </div>

      {application.review_notes && (
        <p className="mt-6 max-w-xl mx-auto text-[#A8B5A0] text-sm leading-relaxed italic">
          "{application.review_notes}"
        </p>
      )}

      {application.status === "approved" && (
        <a
          href="/members/me"
          data-testid="edit-profile-cta"
          className="mt-8 inline-block border border-[#4ADE80] text-[#4ADE80] hover:bg-[#4ADE80]/15 px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
        >
          Edit your character profile →
        </a>
      )}
    </div>
  );
}

function MetaCell({ label, value, last = false }) {
  return (
    <div className={`px-5 py-3 ${!last ? "border-b sm:border-b-0 sm:border-r border-[#283524]" : ""}`}>
      <div className="label-tag mb-1">{label}</div>
      <div className="font-mono-ui text-sm text-[#E2E4DF]">{value}</div>
    </div>
  );
}

function FormSection({ step, title, children }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <span className="font-mono-ui text-xs text-[#C0A875] tracking-[0.25em]">// {step}</span>
        <h3 className="font-display uppercase text-[#E2E4DF] text-2xl tracking-tight">
          {title}
        </h3>
        <div className="flex-1 h-px bg-[#283524]" />
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>;
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="block mb-2 font-mono-ui text-xs tracking-[0.18em] uppercase text-[#E2E4DF] font-bold">
        {label}{" "}
        {required && (
          <span className="text-[#C0A875] font-black text-base align-middle ml-0.5">*</span>
        )}
      </span>
      {hint && (
        <span className="block mb-3 -mt-1 text-[13px] text-[#A8B5A0] leading-relaxed">
          {hint}
        </span>
      )}
      {children}
    </label>
  );
}

const inputClasses =
  "w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-4 py-3 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none transition-colors placeholder:text-[#8D9A87]/50";

function Input(props) {
  return <input {...props} className={inputClasses} />;
}
function Textarea(props) {
  return <textarea {...props} className={`${inputClasses} resize-y leading-relaxed`} />;
}
function Select({ children, ...props }) {
  return (
    <select {...props} className={inputClasses}>
      {children}
    </select>
  );
}


function CharCounter({ value, min = 0, suggested }) {
  const len = (value || "").length;
  const metMin = min === 0 || len >= min;
  const metSuggested = suggested ? len >= suggested : metMin;
  let color = "text-[#C0392B]";
  if (metSuggested) color = "text-[#9CC279]";
  else if (metMin) color = "text-[#C0A875]";
  const target = min ? `min ${min}` : `suggested ${suggested}+`;
  const both = min && suggested ? `min ${min} · suggested ${suggested}+` : target;
  return (
    <div
      className={`mt-1 font-mono-ui text-[10px] tracking-[0.15em] uppercase ${color}`}
      data-testid="char-counter"
    >
      {len} chars · {both}{metSuggested ? " ✓" : ""}
    </div>
  );
}

function PasswordInput(props) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        {...props}
        type={shown ? "text" : "password"}
        autoComplete="off"
        className={`${inputClasses} pr-12`}
      />
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? "Hide password" : "Show password"}
        data-testid="password-toggle"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#8D9A87] hover:text-[#C0A875]"
      >
        {shown ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function CheckRow({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-4 cursor-pointer group" data-testid={`${testid}-row`}>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        data-testid={testid}
        onClick={() => onChange(!checked)}
        className={`mt-0.5 w-5 h-5 border-2 flex items-center justify-center shrink-0 transition-colors ${
          checked
            ? "border-[#C0A875] bg-[#C0A875]"
            : "border-[#5C6B51] bg-transparent group-hover:border-[#C0A875]"
        }`}
      >
        {checked && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6L5 9L10 3" stroke="#0D110C" strokeWidth="2" />
          </svg>
        )}
      </button>
      <span className="text-[#E2E4DF] text-sm leading-relaxed">{label}</span>
    </label>
  );
}

function ThumbToggle({ value, onChange }) {
  const Btn = ({ choice, Icon, testid }) => {
    const active = value === choice;
    return (
      <button
        type="button"
        data-testid={testid}
        aria-pressed={active}
        onClick={() => onChange(choice)}
        className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 border font-mono-ui text-xs uppercase tracking-[0.25em] transition-colors ${
          active
            ? choice
              ? "border-[#4ADE80] bg-[#4ADE80]/15 text-[#4ADE80]"
              : "border-[#C0392B] bg-[#C0392B]/15 text-[#C0392B]"
            : "border-[#283524] bg-[#0D110C] text-[#8D9A87] hover:border-[#5C6B51] hover:text-[#E2E4DF]"
        }`}
      >
        <Icon size={16} />
        {choice ? "Yes" : "No"}
      </button>
    );
  };
  return (
    <div className="flex flex-col sm:flex-row gap-3" data-testid="field-semi-serious-rp">
      <Btn choice={true} Icon={ThumbsUp} testid="semi-serious-yes" />
      <Btn choice={false} Icon={ThumbsDown} testid="semi-serious-no" />
    </div>
  );
}


/**
 * Shown to approved survivors only — lets them propose founding a new faction.
 * On admin approval, the submitter is auto-assigned as the faction's leader.
 */
function CreateFactionBlock() {
  const { user } = useDiscord();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [ledFaction, setLedFaction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", acronym: "", color: "", description: "" });

  // Fetch the user's existing proposal (if any) and check if they already lead a faction.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prop, factions] = await Promise.all([
          api.get("/factions/proposals/mine"),
          api.get("/factions"),
        ]);
        if (cancelled) return;
        setProposal(prop.data?.proposal || null);
        const led = (factions.data?.factions || []).find(
          (f) => f.leader_discord_id === user?.id
        );
        setLedFaction(led || null);
      } catch {
        /* swallow — initial load failures shouldn't block the page */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const reset = () =>
    setForm({ name: "", acronym: "", color: "", description: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const acronym = form.acronym.trim();
    const color = form.color.trim();
    const description = form.description.trim();
    if (name.length < 3) {
      toast.error("Faction name must be at least 3 characters.");
      return;
    }
    if (!acronym) {
      toast.error("Acronym is required.");
      return;
    }
    if (!color) {
      toast.error("Pick a colour.");
      return;
    }
    if (description.length < 25) {
      toast.error(`Description needs at least 25 characters (you have ${description.length}).`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/factions/proposals", { name, acronym, color, description });
      setProposal(res.data?.proposal || null);
      reset();
      toast.success("Faction proposal submitted. The admin will review it shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-10 text-center py-10" data-testid="faction-block-loading">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={20} />
      </div>
    );
  }

  // Already a leader — show a tidy callout instead of the form.
  if (ledFaction) {
    return (
      <div
        data-testid="faction-block-already-leader"
        className="mt-10 bg-[#161D14] border border-[#5C6B51] p-8 text-center"
      >
        <Crown className="text-[#C0A875] mx-auto" size={32} />
        <span className="block label-tag mt-4 text-[#C0A875]">// FACTION LEADER</span>
        <h3 className="mt-2 font-display uppercase text-[#E2E4DF] text-2xl sm:text-3xl">
          You already lead {ledFaction.name}.
        </h3>
        <p className="mt-3 text-[#8D9A87] max-w-md mx-auto text-sm leading-relaxed">
          One faction per leader. Manage your roster from your character page.
        </p>
        <a
          href="/members/me"
          data-testid="faction-block-manage-cta"
          className="mt-6 inline-block border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
        >
          Manage your faction →
        </a>
      </div>
    );
  }

  // Has a non-pending proposal? Surface its status and (if not approved) let them re-submit.
  const status = proposal?.status;

  if (status === "pending") {
    return <FactionProposalStatusCard proposal={proposal} />;
  }

  // approved or rejected — show status card; rejected also re-opens the form below.
  const showForm = !proposal || status === "rejected";

  return (
    <div className="mt-10 space-y-10" data-testid="create-faction-block">
      {proposal && status !== "pending" && (
        <FactionProposalStatusCard proposal={proposal} />
      )}

      {showForm && (
        <form
          onSubmit={onSubmit}
          noValidate
          data-testid="create-faction-form"
          className="bg-[#161D14] border border-[#283524] p-6 sm:p-10 space-y-8"
        >
          <header className="flex items-start gap-4 border-b border-[#283524] pb-5">
            <div className="w-12 h-12 border border-[#C0A875] flex items-center justify-center shrink-0">
              <Flag className="text-[#C0A875]" size={20} />
            </div>
            <div>
              <span className="label-tag">// 06 FOUND A FACTION</span>
              <h3 className="mt-2 font-display uppercase text-[#E2E4DF] text-2xl sm:text-3xl tracking-tight">
                Raise your own flag.
              </h3>
              <p className="mt-2 text-[#8D9A87] text-sm leading-relaxed max-w-xl">
                Want to lead instead of follow? Pitch a faction — name, colours, doctrine. If the
                admin greenlights it, you'll be assigned leader automatically.
              </p>
            </div>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Faction name" required>
              <Input
                data-testid="faction-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Ones Who Live"
                maxLength={60}
                required
              />
            </Field>
            <Field label="Acronym" required hint="1–7 letters / digits / dots (e.g. O.W.L).">
              <Input
                data-testid="faction-form-acronym"
                value={form.acronym}
                onChange={(e) => setForm({ ...form, acronym: e.target.value.toUpperCase() })}
                placeholder="O.W.L"
                maxLength={7}
                required
              />
            </Field>
          </div>

          <Field
            label="Colour"
            required
            hint="This is the badge / accent colour. Picked colours render against the dark theme."
          >
            <div className="flex items-center gap-3 mb-3">
              {(form.acronym || form.name) && (
                <FactionChip
                  faction={{
                    acronym: form.acronym || form.name.slice(0, 5).toUpperCase(),
                    color: form.color || null,
                    name: form.name,
                  }}
                  size="sm"
                  testid="faction-form-preview-chip"
                />
              )}
              {form.color && (
                <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
                  Selected: <span className="text-[#E2E4DF]">{form.color}</span>
                </span>
              )}
            </div>
            <ColorSwatchPicker
              value={form.color}
              onChange={(c) => setForm({ ...form, color: c })}
            />
          </Field>

          <Field
            label="Description"
            required
            hint="What does this faction stand for? Who do they protect, who do they fight? Min 25 chars."
          >
            <Textarea
              data-testid="faction-form-description"
              rows={5}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              maxLength={1000}
              required
            />
            <CharCounter value={form.description} min={25} suggested={100} />
          </Field>

          <div className="pt-4 border-t border-[#283524] flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div className="font-mono-ui text-[10px] text-[#8D9A87] uppercase tracking-[0.25em]">
              // PROPOSING AS{" "}
              <span className="text-[#C0A875]">@{user?.username || "—"}</span>
            </div>
            <button
              type="submit"
              data-testid="faction-form-submit"
              disabled={submitting}
              className="bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-8 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold transition-colors inline-flex items-center gap-2"
            >
              {submitting ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              {submitting ? "Submitting…" : "Submit Faction Proposal"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function FactionProposalStatusCard({ proposal }) {
  const submitted = proposal.submitted_at
    ? new Date(proposal.submitted_at).toLocaleString()
    : null;
  const reviewed = proposal.reviewed_at
    ? new Date(proposal.reviewed_at).toLocaleString()
    : null;

  const layouts = {
    pending: {
      Icon: Clock,
      iconColor: "text-[#C0A875]",
      borderColor: "border-[#C0A875]",
      eyebrow: "// FACTION PROPOSAL IN REVIEW",
      eyebrowColor: "text-[#C0A875]",
      headline: "Your faction proposal is in the queue.",
      body:
        "The admin reviews proposals manually. You'll be notified in Discord. Only one pending proposal at a time.",
    },
    approved: {
      Icon: CheckCircle2,
      iconColor: "text-[#4ADE80]",
      borderColor: "border-[#4ADE80]",
      eyebrow: "// FACTION APPROVED",
      eyebrowColor: "text-[#4ADE80]",
      headline: `${proposal.name} is live.`,
      body:
        "Congratulations — you've been auto-assigned as leader. Manage your roster from your character page.",
    },
    rejected: {
      Icon: XCircle,
      iconColor: "text-[#C0392B]",
      borderColor: "border-[#C0392B]",
      eyebrow: "// FACTION PROPOSAL DECLINED",
      eyebrowColor: "text-[#C0392B]",
      headline: "This pitch didn't land.",
      body:
        "Your last faction proposal wasn't accepted. You can revise and submit a new one below.",
    },
  };
  const layout = layouts[proposal.status] || layouts.pending;
  const { Icon } = layout;

  return (
    <div
      data-testid="faction-proposal-status"
      className={`mt-10 bg-[#161D14] border ${layout.borderColor} p-8 text-center`}
    >
      <Icon className={`${layout.iconColor} mx-auto`} size={40} />
      <span className={`block label-tag mt-4 ${layout.eyebrowColor}`}>{layout.eyebrow}</span>
      <h3 className="mt-2 font-display uppercase text-[#E2E4DF] text-2xl sm:text-3xl">
        {layout.headline}
      </h3>
      <p className="mt-3 text-[#8D9A87] max-w-xl mx-auto text-sm leading-relaxed">{layout.body}</p>

      <div className="mt-6 inline-flex flex-col sm:flex-row items-stretch border border-[#283524] bg-[#0D110C]">
        <MetaCell label="Name" value={proposal.name || "—"} />
        <MetaCell label="Acronym" value={proposal.acronym || "—"} />
        {submitted && <MetaCell label="Submitted" value={submitted} />}
        {reviewed && <MetaCell label="Reviewed" value={reviewed} last />}
      </div>

      {proposal.review_notes && (
        <p className="mt-5 max-w-xl mx-auto text-[#A8B5A0] text-sm leading-relaxed italic">
          "{proposal.review_notes}"
        </p>
      )}

      {proposal.status === "approved" && (
        <a
          href="/members/me"
          data-testid="faction-proposal-manage-cta"
          className="mt-6 inline-block border border-[#4ADE80] text-[#4ADE80] hover:bg-[#4ADE80]/15 px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
        >
          Manage your faction →
        </a>
      )}
    </div>
  );
}

function ColorSwatchPicker({ value, onChange }) {
  return (
    <div
      data-testid="faction-form-color-grid"
      className="grid gap-1.5 p-2 bg-[#0D110C] border border-[#283524]"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(28px, 1fr))" }}
    >
      {FACTION_COLORS.map((c) => {
        const selected = (value || "").toLowerCase() === c.toLowerCase();
        return (
          <button
            type="button"
            key={c}
            title={c}
            data-testid={`faction-form-color-${c.toLowerCase()}`}
            onClick={() => onChange(c)}
            style={{ background: c }}
            className={`h-7 w-full border-2 transition-all ${
              selected
                ? "border-[#E2E4DF] scale-110 z-10 relative shadow-[0_0_0_2px_#0D110C]"
                : "border-[#0D110C] hover:border-[#C0A875]"
            }`}
            aria-label={c}
          >
            {selected && (
              <span
                aria-hidden
                className="block w-full h-full"
                style={{
                  color: factionTextColor(c),
                  textAlign: "center",
                  lineHeight: "100%",
                  fontWeight: 900,
                  fontSize: "11px",
                }}
              >
                ✓
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
